{
  "name": "devportal",
  "displayName": "Field Editorial",
  "description": "A compact editorial theme — Helvetica in near-black ink with a burnt-orange accent, tight condensed titles, letterspaced labels, warm tinted fills and hairline rules",
  "whenToUse": "Developer and product documentation, API guides and technical handbooks: dense text, code and tables that need compact, letterspaced structure.",
  "version": "4.0.0",
  "colors": {
    "primary": "#12191F",
    "secondary": "#172028",
    "accent": "#E35B3F",
    "text": "#232323",
    "background": "#FFFFFF",
    "border": "#E2E0DD",
    "textPrimary": "#232323",
    "textSecondary": "#46494C",
    "textMuted": "#737373",
    "borderPrimary": "#D8D5D1",
    "borderSecondary": "#F2F3F3",
    "backgroundPrimary": "#FFFFFF",
    "backgroundSecondary": "#F7ECE7",
    "accent4": "#46494C",
    "accent5": "#8A9299",
    "accent6": "#E8A18D"
  },
  "palette": {
    "rule": "#D8D5D1",
    "textMuted": "#737373",
    "onPrimary": "#FFFFFF",
    "surfaceInverse": "#12191F",
    "positive": "#2E7D4F",
    "negative": "#B42318",
    "chart": ["accent", "primary", "accent4", "accent5", "accent6", "rule"]
  },
  "fonts": {
    "heading": {
      "family": "Helvetica",
      "size": 23
    },
    "body": {
      "family": "Helvetica",
      "size": 9.5
    },
    "mono": {
      "family": "Courier New",
      "size": 10
    },
    "light": {
      "family": "Helvetica",
      "size": 23
    }
  },
  "page": {
    "size": "A4",
    "margins": {
      "top": 1080,
      "bottom": 1080,
      "left": 1080,
      "right": 1080,
      "header": 720,
      "footer": 720,
      "gutter": 0
    }
  },
  "typography": {
    "roles": {
      "eyebrow": {
        "face": "heading",
        "size": 8,
        "weight": 700,
        "case": "upper",
        "tracking": 10,
        "color": "accent",
        "spaceAfter": 4
      },
      "display": {
        "face": "heading",
        "weight": 700,
        "color": "primary",
        "lineHeight": 1.05,
        "spaceAfter": 8,
        "tracking": -2
      },
      "stat": {
        "face": "heading",
        "weight": 700,
        "color": "accent",
        "lineHeight": 1
      },
      "quote": {
        "face": "body",
        "color": "textSecondary",
        "lineHeight": 1.3
      },
      "label": {
        "face": "body",
        "size": 9,
        "weight": 700,
        "color": "textSecondary",
        "case": "upper",
        "tracking": 6
      },
      "footer": {
        "face": "body",
        "size": 8,
        "color": "textMuted"
      },
      "tableHeader": {
        "face": "body",
        "size": 9,
        "weight": 700,
        "color": "secondary"
      },
      "tableCell": {
        "face": "body",
        "size": 9.5,
        "color": "textPrimary"
      },
      "chartLabel": {
        "face": "body",
        "size": 9,
        "weight": 400,
        "color": "textSecondary"
      },
      "tracker": {
        "face": "body",
        "size": 8,
        "case": "upper",
        "tracking": 6,
        "color": "textMuted"
      },
      "source": {
        "face": "body",
        "size": 8,
        "color": "textMuted"
      }
    },
    "scale": {
      "a4": {
        "base": 9.5,
        "ratio": 1.2,
        "baselinePt": 2
      },
      "letter": {
        "base": 9.5,
        "ratio": 1.2,
        "baselinePt": 2
      }
    }
  },
  "spacing": {
    "basePt": 7,
    "blockGap": {
      "tight": 3,
      "normal": 7,
      "loose": 14
    },
    "canvas": {
      "a4": {
        "safeAreaIn": 0.75,
        "gutterIn": 0,
        "columns": 12
      },
      "letter": {
        "safeAreaIn": 0.75,
        "gutterIn": 0,
        "columns": 12
      }
    }
  },
  "chrome": {
    "runningHead": {
      "type": "tracker",
      "color": "textMuted",
      "rule": {
        "weightPt": 0.5,
        "color": "rule"
      },
      "alignment": "right"
    },
    "tracker": {
      "type": "tracker",
      "color": "textMuted",
      "alignment": "right"
    },
    "actionTitle": {
      "type": "display",
      "color": "primary"
    },
    "keyTakeaways": {
      "type": "label",
      "color": "primary",
      "rule": {
        "weightPt": 1,
        "color": "accent"
      },
      "padPt": 8
    },
    "sourceLine": {
      "type": "source",
      "color": "textMuted"
    },
    "confidentialFooter": {
      "type": "footer",
      "color": "textMuted",
      "rule": {
        "weightPt": 0.5,
        "color": "rule"
      },
      "alignment": "left"
    },
    "logoSlot": {
      "alignment": "right"
    },
    "cover": {
      "type": "display",
      "color": "primary",
      "rule": {
        "weightPt": 3,
        "color": "accent"
      }
    }
  },
  "motif": {
    "kind": "rule",
    "color": "accent",
    "weightPt": 2,
    "placement": "top"
  },
  "styles": {
    "normal": {
      "font": "body",
      "color": "textPrimary",
      "lineSpacing": {
        "type": "multiple",
        "value": 1.3
      },
      "alignment": "left",
      "spacing": {
        "after": 7
      }
    },
    "title": {
      "font": "heading",
      "size": 29,
      "bold": true,
      "color": "primary",
      "alignment": "left",
      "lineSpacing": {
        "type": "multiple",
        "value": 1.02
      },
      "characterSpacing": {
        "type": "condensed",
        "value": 8
      },
      "spacing": {
        "before": 0,
        "after": 10
      }
    },
    "subtitle": {
      "font": "body",
      "size": 10.5,
      "italic": false,
      "color": "textSecondary",
      "alignment": "left",
      "lineSpacing": {
        "type": "multiple",
        "value": 1.25
      },
      "spacing": {
        "before": 0,
        "after": 12
      }
    },
    "heading1": {
      "font": "heading",
      "size": 21,
      "bold": true,
      "color": "primary",
      "lineSpacing": {
        "type": "multiple",
        "value": 1.15
      },
      "spacing": {
        "before": 26,
        "after": 12
      },
      "keepNext": true,
      "alignment": "left",
      "borders": {
        "bottom": {
          "style": "single",
          "size": 4,
          "color": "accent",
          "space": 6
        }
      }
    },
    "heading2": {
      "font": "heading",
      "size": 15.5,
      "bold": true,
      "color": "secondary",
      "lineSpacing": {
        "type": "multiple",
        "value": 1.15
      },
      "spacing": {
        "before": 20,
        "after": 8
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading3": {
      "font": "body",
      "size": 12.5,
      "bold": true,
      "color": "secondary",
      "spacing": {
        "before": 14,
        "after": 6
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading4": {
      "font": "body",
      "size": 11.5,
      "bold": true,
      "color": "textPrimary",
      "spacing": {
        "before": 12,
        "after": 4
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading5": {
      "font": "mono",
      "size": 10.5,
      "bold": true,
      "color": "secondary",
      "spacing": {
        "before": 10,
        "after": 4
      },
      "keepNext": true,
      "alignment": "left"
    },
    "heading6": {
      "font": "body",
      "size": 10.5,
      "bold": false,
      "color": "textSecondary",
      "characterSpacing": {
        "type": "expanded",
        "value": 20
      },
      "spacing": {
        "before": 10,
        "after": 4
      },
      "keepNext": true,
      "alignment": "left"
    },
    "TOC1": {
      "font": "body",
      "size": 11,
      "bold": true,
      "color": "primary",
      "spacing": {
        "before": 5,
        "after": 3
      },
      "tabStops": [
        {
          "type": "right",
          "position": 9746,
          "leader": "dot"
        }
      ]
    },
    "TOC2": {
      "font": "body",
      "size": 10.5,
      "color": "textPrimary",
      "indent": {
        "left": 240
      },
      "spacing": {
        "after": 2
      },
      "tabStops": [
        {
          "type": "right",
          "position": 9746,
          "leader": "dot"
        }
      ]
    },
    "TOC3": {
      "font": "body",
      "size": 10,
      "color": "textSecondary",
      "indent": {
        "left": 480
      },
      "spacing": {
        "after": 2
      },
      "tabStops": [
        {
          "type": "right",
          "position": 9746,
          "leader": "dot"
        }
      ]
    }
  },
  "componentDefaults": {
    "table": {
      "width": 100,
      "borderColor": "#E2E0DD",
      "borderSize": 0.5,
      "hideBorders": {
        "left": true,
        "right": true,
        "insideVertical": true
      },
      "cellDefaults": {
        "padding": {
          "top": 3,
          "bottom": 3
        }
      },
      "headerCellDefaults": {
        "font": {
          "bold": true
        },
        "color": "#172028",
        "backgroundColor": "#F7ECE7",
        "borderColor": {
          "bottom": "#D8D5D1"
        },
        "borderSize": {
          "bottom": 1
        }
      }
    },
    "list": {
      "format": "bullet",
      "bullet": "—",
      "spacing": {
        "item": 2
      }
    },
    "image": {
      "alignment": "center"
    },
    "statistic": {
      "alignment": "center"
    },
    "section": {
      "pageBreak": false
    }
  }
}
